import React, { useState, useEffect } from 'react';
import { Employee, EmployeeFormData, Job, Department } from '../../types/models';
import { EmployeeService } from '../../services/employeeService';
import { JobService } from '../../services/jobService';
import { DepartmentService } from '../../services/departmentService';
import { formatDateForInput } from '../../utils/dateUtils';

interface EmployeeFormProps {
  employee?: Employee;
  departmentId?: number;
  onSuccess: () => void;
  onCancel: () => void;
}

export const EmployeeForm: React.FC<EmployeeFormProps> = ({
  employee,
  departmentId,
  onSuccess,
  onCancel
}) => {
  const [formData, setFormData] = useState<EmployeeFormData>({
    surname: employee?.surname || '',
    name: employee?.name || '',
    patronymic: employee?.patronymic || '',
    birthday: employee?.birthday,
    educationDocumentNumber: employee?.educationDocumentNumber || '',
    departmentId: employee?.departmentId || departmentId || 0,
    jobIds: employee?.employeeJobCross?.map(ejc => ejc.jobId) || [],
    mainJobId: employee?.employeeJobCross?.find(ejc => ejc.isMain)?.jobId,
  });

  const [availableJobs, setAvailableJobs] = useState<Job[]>([]);
  const [availableDepartments, setAvailableDepartments] = useState<Department[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadJobs();
    loadDepartments();
  }, []);

  const loadJobs = async () => {
    try {
      const response = await JobService.getAll();
      if (response.success) {
        setAvailableJobs(response.data);
      }
    } catch (err) {
      console.error('Ошибка загрузки должностей:', err);
    }
  };

  const loadDepartments = async () => {
    try {
      const response = await DepartmentService.getAll();
      if (response.success) {
        setAvailableDepartments(response.data);
      }
    } catch (err) {
      console.error('Ошибка загрузки отделов:', err);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    // Валидация: если выбраны должности, должна быть указана одна основная
    if (formData.jobIds.length > 0 && !formData.mainJobId) {
      setError('Выберите основную должность.');
      setLoading(false);
      return;
    }

    try {
      let response;
      if (employee) {
        // Обновление
        response = await EmployeeService.update(employee.id, { ...formData, id: employee.id });
      } else {
        // Создание
        response = await EmployeeService.create(formData);
      }

      if (response.success) {
        onSuccess();
      } else {
        setError(response.message || 'Ошибка сохранения');
      }
    } catch (err) {
      setError('Произошла ошибка при сохранении');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: name === 'departmentId' ? Number(value) : 
              name === 'birthday' ? value : value
    }));
  };

  const handleJobChange = (jobId: number, checked: boolean) => {
    setFormData(prev => {
      const jobIds = checked
        ? [...prev.jobIds, jobId]
        : prev.jobIds.filter(id => id !== jobId);

      // Если сняли выбор с основной должности, сбрасываем mainJobId
      const mainJobId = prev.mainJobId === jobId && !checked ? undefined : prev.mainJobId;

      return { ...prev, jobIds, mainJobId };
    });
  };

  const handleMainJobChange = (jobId: number) => {
    setFormData(prev => ({
      ...prev,
      mainJobId: prev.mainJobId === jobId ? undefined : jobId
    }));
  };

  return (
    <div className="modal-overlay">
      <div
        className="modal-content"
        style={{ maxWidth: '800px' }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="card" style={{ margin: 0, boxShadow: 'none' }}>
          <h3>{employee ? 'Редактирование сотрудника' : 'Создание сотрудника'}</h3>

          {error && (
            <div className="alert alert-error">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label className="form-label">Фамилия *</label>
            <input
              type="text"
              name="surname"
              className="form-input"
              value={formData.surname}
              onChange={handleChange}
              required
            />
          </div>

          <div className="form-group">
            <label className="form-label">Имя *</label>
            <input
              type="text"
              name="name"
              className="form-input"
              value={formData.name}
              onChange={handleChange}
              required
            />
          </div>

          <div className="form-group">
            <label className="form-label">Отчество</label>
            <input
              type="text"
              name="patronymic"
              className="form-input"
              value={formData.patronymic}
              onChange={handleChange}
            />
          </div>

        <div className="form-group">
          <label className="form-label">Дата рождения</label>
          <input
            type="date"
            name="birthday"
            className="form-input"
            value={formatDateForInput(formData.birthday)}
            onChange={handleChange}
          />
        </div>

        <div className="form-group">
          <label className="form-label">Номер документа об образовании</label>
          <input
            type="text"
            name="educationDocumentNumber"
            className="form-input"
            value={formData.educationDocumentNumber || ''}
            onChange={handleChange}
            placeholder="Введите номер документа об образовании"
          />
        </div>

        <div className="form-group">
          <label className="form-label">Структурное подразделение *</label>
          <select
            name="departmentId"
            className="form-input"
            value={formData.departmentId}
            onChange={handleChange}
            required
          >
            <option value="">Выберите структурное подразделение</option>
            {availableDepartments.map(department => (
              <option key={department.id} value={department.id}>
                {department.name}
              </option>
            ))}
          </select>
        </div>

        <div className="form-group">
          <label className="form-label">Должности</label>
          <div style={{ maxHeight: '200px', overflowY: 'auto', border: '1px solid #ddd', padding: '10px', borderRadius: '4px' }}>
            {availableJobs.map((job) => {
              const isSelected = formData.jobIds.includes(job.id);
              const isMain = formData.mainJobId === job.id;

              return (
                <div key={job.id} style={{ display: 'flex', alignItems: 'center', marginBottom: '5px' }}>
                  <input
                    type="checkbox"
                    id={`job-${job.id}`}
                    checked={isSelected}
                    onChange={(e) => handleJobChange(job.id, e.target.checked)}
                  />
                  <label htmlFor={`job-${job.id}`} style={{ marginLeft: '5px', marginRight: '10px' }}>
                    {job.name}
                  </label>
                  {isSelected && (
                    <input
                      type="radio"
                      name="mainJob"
                      checked={isMain}
                      onChange={() => handleMainJobChange(job.id)}
                      title="Основная должность"
                    />
                  )}
                  {isSelected && <span style={{ fontSize: '12px', color: '#666' }}>основная</span>}
                </div>
              );
            })}
          </div>
          {formData.jobIds.length > 0 && !formData.mainJobId && (
            <div style={{ color: '#d32f2f', marginTop: '6px' }}>Необходимо выбрать основную должность.</div>
          )}
          <small style={{ color: '#666' }}>
            Отметьте галочкой должности сотрудника. Выберите основную должность радиокнопкой.
          </small>
        </div>

        <div style={{ display: 'flex', gap: '10px', marginTop: '20px' }}>
          <button
            type="submit"
            className="btn btn-primary"
            disabled={loading || (formData.jobIds.length > 0 && !formData.mainJobId)}
          >
            {loading ? 'Сохранение...' : (employee ? 'Сохранить' : 'Создать')}
          </button>
          <button
            type="button"
            className="btn btn-secondary"
            onClick={onCancel}
            disabled={loading}
          >
            Отмена
          </button>
        </div>
      </form>
        </div>
      </div>
    </div>
  );
};

